import { useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import {
  useCreateEmployeeDocumentType,
  useEmployeeDocumentTypes,
  useUpdateEmployeeDocumentType,
} from "../api/employeeDocs";
import type { EmployeeDocumentType } from "../types.employee_docs";

type SortKey = "name" | "is_active" | "sort_order" | "created_by_name" | "created_at";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("pl-PL");
}

interface TypeFormState {
  name: string;
  sort_order: string;
  is_active: boolean;
}

function TypeFormModal({
  initial,
  onClose,
}: {
  initial: EmployeeDocumentType | null;
  onClose: () => void;
}) {
  const isEdit = initial !== null;
  const create = useCreateEmployeeDocumentType();
  const update = useUpdateEmployeeDocumentType();

  const [form, setForm] = useState<TypeFormState>({
    name:       initial?.name              ?? "",
    sort_order: String(initial?.sort_order ?? 0),
    is_active:  initial?.is_active         ?? true,
  });
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { setError("Nazwa jest wymagana."); return; }
    setError(null);

    const payload = {
      name:       form.name.trim(),
      sort_order: parseInt(form.sort_order) || 0,
      is_active:  form.is_active,
    };

    if (isEdit && initial) {
      update.mutate({ id: initial.id, ...payload }, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  }

  const isPending = create.isPending || update.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Edytuj typ dokumentu" : "Dodaj typ dokumentu"}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="p-4 space-y-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">
            Nazwa *
          </span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="np. Umowa B2B"
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">
            Kolejność sortowania
          </span>
          <input
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </label>

        {isEdit && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="accent-[var(--color-primary)]"
            />
            <span className="text-sm text-[var(--color-text-secondary)]">Aktywny</span>
          </label>
        )}

        {error && <p className="text-xs text-red-600">{error}</p>}
        {(create.isError || update.isError) && (
          <p className="text-xs text-red-600">Błąd zapisu. Spróbuj ponownie.</p>
        )}

        <div className="flex gap-2 pt-1">
          <Button type="submit" size="sm" disabled={isPending}>
            {isPending ? "Zapisywanie…" : isEdit ? "Zapisz zmiany" : "Dodaj"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function DocumentTypeManagerPage() {
  const { data: types = [], isLoading } = useEmployeeDocumentTypes();

  const [showModal, setShowModal] = useState(false);
  const [editType, setEditType] = useState<EmployeeDocumentType | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("sort_order");
  const [sortAsc, setSortAsc] = useState(true);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  }

  function SortTh({ col, label }: { col: SortKey; label: string }) {
    const active = sortKey === col;
    return (
      <th
        onClick={() => toggleSort(col)}
        className="px-4 py-2.5 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide cursor-pointer select-none hover:text-[var(--color-text-primary)]"
      >
        {label}
        {active && <span className="ml-1">{sortAsc ? "↑" : "↓"}</span>}
      </th>
    );
  }

  const sorted = [...types].sort((a, b) => {
    let av: string | number | boolean = a[sortKey as keyof EmployeeDocumentType] ?? "";
    let bv: string | number | boolean = b[sortKey as keyof EmployeeDocumentType] ?? "";
    av = String(av);
    bv = String(bv);
    return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Typy dokumentów pracownika
          </h1>
          <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
            Słownik typów dokumentów dostępnych w repozytorium. Bez usuwania — dezaktywuj nieużywane.
          </p>
        </div>
        <Button onClick={() => { setEditType(null); setShowModal(true); }}>
          + Dodaj typ
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-[var(--color-text-tertiary)]">Ładowanie…</div>
      ) : (
        <div className="rounded-lg border border-[var(--color-border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-bg-elevated)] border-b border-[var(--color-border)]">
              <tr>
                <SortTh col="name" label="Nazwa" />
                <SortTh col="is_active" label="Aktywny" />
                <SortTh col="created_by_name" label="Dodał" />
                <SortTh col="created_at" label="Data dodania" />
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr
                  key={t.id}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <td className="px-4 py-2.5 font-medium text-[var(--color-text-primary)]">
                    {t.name}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge color={t.is_active ? "green" : "gray"}>
                      {t.is_active ? "Tak" : "Nie"}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">
                    {t.created_by_name ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--color-text-secondary)]">
                    {formatDate(t.created_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setEditType(t); setShowModal(true); }}
                    >
                      Edytuj
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TypeFormModal
          initial={editType}
          onClose={() => { setShowModal(false); setEditType(null); }}
        />
      )}
    </div>
  );
}
